import {
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText,
  OutlinedInput, Box, Chip,
} from '@mui/material';
import { CK, FONT_BODY, statusChipSx } from '../portalStyles';

// Shared multi-select for account / region pickers across the Security Hub +
// GuardDuty portal. Built on MUI Select (multiple) — the same proven pattern the
// original region pickers used — with a checkbox per option, a "Select all /
// Deselect all" row, name chips for the current selection, and locked values that
// cannot be deselected (e.g. the home region is always a linking region).
//
// Props:
//   label        string
//   options      Array<{ value: string, label: string }>  (label is shown to the user)
//   value        string[]                                  (selected values)
//   onChange     (next: string[]) => void
//   selectAll    boolean  (default true) — show the Select all row
//   lockedValues string[] (default []) — always-selected, non-removable values
//   fullWidth    boolean  (default true)
//   sx           passthrough to the FormControl

const SELECT_ALL = '__select_all__';

// A selection can run to dozens of long labels ("Asia Pacific (Mumbai) —
// ap-south-1", "Name (123456789012)"). Summarize instead of growing the control
// to six chip rows and pushing the Save button below the fold.
const MAX_SUMMARY_CHIPS = 6;

export default function MultiSelectField({
  label,
  options,
  value,
  onChange,
  selectAll = true,
  lockedValues = [],
  fullWidth = true,
  sx,
}) {
  const optionValues = options.map((o) => o.value);
  const allSelected = optionValues.length > 0 && optionValues.every((v) => value.includes(v));
  const someSelected = value.some((v) => optionValues.includes(v));
  const labelFor = (v) => options.find((o) => o.value === v)?.label ?? v;
  const withLocked = (vals) => [...new Set([...lockedValues, ...vals])];

  const handleChange = (e) => {
    // e.target.value is the array of selected primitive values (MUI Select multiple).
    const raw = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    if (raw.includes(SELECT_ALL)) {
      onChange(allSelected ? [...lockedValues] : withLocked(optionValues));
      return;
    }
    onChange(withLocked(raw));
  };

  return (
    <FormControl fullWidth={fullWidth} sx={sx}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          const shown = selected.slice(0, MAX_SUMMARY_CHIPS);
          const extra = selected.length - shown.length;
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {shown.map((v) => <Chip key={v} label={labelFor(v)} size="small" sx={statusChipSx('neutral')} />)}
              {extra > 0 && (
                <Chip key="__more__" label={`+${extra} more`} size="small" sx={statusChipSx('neutral')} />
              )}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 360,
              borderRadius: '8px',
              border: `1px solid ${CK.border}`,
            },
          },
        }}
      >
        {selectAll && (
          <MenuItem value={SELECT_ALL}>
            <Checkbox checked={allSelected} indeterminate={!allSelected && someSelected} />
            <ListItemText
              primary={allSelected ? 'Deselect all' : 'Select all'}
              primaryTypographyProps={{
                sx: { fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 600, color: CK.navy },
              }}
            />
          </MenuItem>
        )}
        {options.map((o) => {
          const locked = lockedValues.includes(o.value);
          return (
            <MenuItem key={o.value} value={o.value} disabled={locked}>
              <Checkbox checked={value.includes(o.value)} disabled={locked} />
              <ListItemText
                primary={o.label}
                primaryTypographyProps={{
                  sx: { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.navy },
                }}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
